// Currency conversion service with real exchange rates

interface ExchangeRate {
  from: string;
  to: string;
  rate: number;
  timestamp: number;
}

interface CurrencyConversionResult {
  originalAmount: number;
  originalCurrency: string;
  convertedAmount: number;
  convertedCurrency: string;
  exchangeRate: number;
  timestamp: number;
}

class CurrencyService {
  private cacheKey = 'exchange_rates';
  private cacheExpiry = 60 * 60 * 1000; // 1 hour cache
  private cachedRates: Map<string, ExchangeRate> = new Map();
  
  // Fallback rates - will be updated from live API on first successful fetch
  private fallbackRates = {
    'USD_TO_GEL': 0, // Will be set from live API
    'GEL_TO_USD': 0  // Will be set from live API
  };
  
  private hasValidFallback = false; // Track if we have valid fallback rates

  /**
   * Convert amount from USD to GEL
   */
  async convertUSDToGEL(usdAmount: number): Promise<CurrencyConversionResult> {
    const rate = await this.getExchangeRate('USD', 'GEL');
    
    // GEL currency typically doesn't use decimals - round to whole number
    const convertedAmount = Math.round(usdAmount * rate.rate);

    return {
      originalAmount: usdAmount,
      originalCurrency: 'USD',
      convertedAmount,
      convertedCurrency: 'GEL',
      exchangeRate: rate.rate,
      timestamp: Date.now()
    };
  }

  /**
   * Convert amount from GEL to USD
   */
  async convertGELToUSD(gelAmount: number): Promise<CurrencyConversionResult> {
    const rate = await this.getExchangeRate('GEL', 'USD');
    
    // USD currency uses 2 decimal places
    const convertedAmount = Math.round(gelAmount * rate.rate * 100) / 100;

    return {
      originalAmount: gelAmount,
      originalCurrency: 'GEL',
      convertedAmount,
      convertedCurrency: 'USD',
      exchangeRate: rate.rate,
      timestamp: Date.now()
    };
  }

  /**
   * Get current exchange rate between two currencies
   */
  private async getExchangeRate(from: string, to: string): Promise<ExchangeRate> {
    const cacheKey = `${from}_TO_${to}`;
    
    // Check cache first
    const cached = this.getCachedRate(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      // Try multiple free exchange rate APIs in order
      let rate = await this.fetchFromExchangeRateAPI(from, to);
      
      if (!rate) {
        rate = await this.fetchFromCurrencyAPI(from, to);
      }
      
      if (!rate) {
        rate = await this.fetchFromFreeCurrencyAPI(from, to);
      }
      
      if (!rate) {
        rate = await this.fetchFromCentralBankAPI(from, to);
      }

      if (rate) {
        // Update fallback rates when we get a successful fetch
        this.updateFallbackFromLiveRate(from, to, rate.rate);
        
        // Cache the result
        this.cacheRate(cacheKey, rate);
        return rate;
      } else {
        // Only use fallback if we have valid rates from previous successful fetches
        if (this.hasValidFallback) {
          console.warn('🔄 Using cached fallback rates from previous successful API calls');
          return this.getFallbackRate(from, to);
        } else {
          throw new Error('No exchange rate data available and no valid fallback rates');
        }
      }

    } catch (error) {
      console.error('Error fetching exchange rate:', error);
      
      if (this.hasValidFallback) {
        console.warn('🔄 Falling back to previously cached rates');
        return this.getFallbackRate(from, to);
      } else {
        // Return a default 1:1 rate as absolute last resort
        console.error('❌ No exchange rate data available! Using 1:1 rate as emergency fallback');
        return {
          from,
          to,
          rate: 1,
          timestamp: Date.now()
        };
      }
    }
  }

  /**
   * Fetch from ExchangeRate-API (free tier: 1500 requests/month)
   */
  private async fetchFromExchangeRateAPI(from: string, to: string): Promise<ExchangeRate | null> {
    try {
      const response = await fetch(
        `https://api.exchangerate-api.com/v4/latest/${from}`,
        { timeout: 5000 } as any
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      
      if (data.rates && data.rates[to]) {
        return {
          from,
          to,
          rate: data.rates[to],
          timestamp: Date.now()
        };
      }
      
      return null;
    } catch (error) {
      console.error('ExchangeRate-API failed:', error);
      return null;
    }
  }

  /**
   * Fetch from CurrencyAPI (backup - free tier: 300 requests/month)
   */
  private async fetchFromCurrencyAPI(from: string, to: string): Promise<ExchangeRate | null> {
    try {
      // Using free currency API as backup
      const response = await fetch(
        `https://cdn.jsdelivr.net/gh/fawazahmed0/currency-api@1/latest/currencies/${from.toLowerCase()}/${to.toLowerCase()}.json`,
        { timeout: 5000 } as any
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const rate = data[to.toLowerCase()];
      
      if (rate) {
        console.log('✅ CurrencyAPI rate fetched successfully');
        return {
          from,
          to,
          rate: rate,
          timestamp: Date.now()
        };
      }
      
      return null;
    } catch (error) {
      console.error('CurrencyAPI failed:', error);
      return null;
    }
  }

  /**
   * Fetch from FreeCurrencyAPI (alternative free source)
   */
  private async fetchFromFreeCurrencyAPI(from: string, to: string): Promise<ExchangeRate | null> {
    try {
      const response = await fetch(
        `https://api.freecurrencyapi.com/v1/latest?base_currency=${from}&currencies=${to}`,
        { timeout: 5000 } as any
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      
      if (data.data && data.data[to]) {
        console.log('✅ FreeCurrencyAPI rate fetched successfully');
        return {
          from,
          to,
          rate: data.data[to],
          timestamp: Date.now()
        };
      }
      
      return null;
    } catch (error) {
      console.error('FreeCurrencyAPI failed:', error);
      return null;
    }
  }

  /**
   * Fetch from National Bank of Georgia API (for GEL rates)
   */
  private async fetchFromCentralBankAPI(from: string, to: string): Promise<ExchangeRate | null> {
    try {
      // Only use for GEL conversions
      if (to !== 'GEL' && from !== 'GEL') {
        return null;
      }

      // National Bank of Georgia API
      const response = await fetch(
        'https://nbg.gov.ge/gw/api/ct/monetarypolicy/currencies/en/json',
        { timeout: 10000 } as any
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      
      // Find USD rate
      const usdRate = data[0]?.currencies?.find((curr: any) => curr.code === 'USD');
      
      if (usdRate && usdRate.rate) {
        console.log('✅ Georgian Central Bank rate fetched successfully');
        
        if (from === 'USD' && to === 'GEL') {
          return {
            from,
            to,
            rate: parseFloat(usdRate.rate),
            timestamp: Date.now()
          };
        } else if (from === 'GEL' && to === 'USD') {
          return {
            from,
            to,
            rate: 1 / parseFloat(usdRate.rate),
            timestamp: Date.now()
          };
        }
      }
      
      return null;
    } catch (error) {
      console.error('Central Bank API failed:', error);
      return null;
    }
  }

  /**
   * Get fallback exchange rate
   */
  private getFallbackRate(from: string, to: string): ExchangeRate {
    const key = `${from}_TO_${to}`;
    const rate = this.fallbackRates[key as keyof typeof this.fallbackRates] || 1;

    console.log(`🔄 Using fallback rate for ${from} → ${to}: ${rate}`);

    return {
      from,
      to,
      rate,
      timestamp: Date.now()
    };
  }

  /**
   * Get cached exchange rate
   */
  private getCachedRate(key: string): ExchangeRate | null {
    try {
      const cached = localStorage.getItem(`${this.cacheKey}_${key}`);
      if (!cached) return null;

      const parsed = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is expired
      if (now - parsed.timestamp > this.cacheExpiry) {
        localStorage.removeItem(`${this.cacheKey}_${key}`);
        return null;
      }

      return parsed;
    } catch (error) {
      console.error('Error reading cached rate:', error);
      return null;
    }
  }

  /**
   * Cache exchange rate
   */
  private cacheRate(key: string, rate: ExchangeRate): void {
    try {
      localStorage.setItem(`${this.cacheKey}_${key}`, JSON.stringify(rate));
    } catch (error) {
      console.error('Error caching exchange rate:', error);
    }
  }

  /**
   * Format currency amount for display
   */
  formatCurrency(amount: number, currency: 'USD' | 'GEL'): string {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: currency === 'GEL' ? 0 : 2, // GEL usually doesn't show decimals
      maximumFractionDigits: currency === 'GEL' ? 0 : 2
    });

    return formatter.format(amount);
  }

  /**
   * Get currency symbol
   */
  getCurrencySymbol(currency: 'USD' | 'GEL'): string {
    return currency === 'USD' ? '$' : '₾';
  }

  /**
   * Clear all cached rates (useful for testing)
   */
  clearCache(): void {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(this.cacheKey)) {
        localStorage.removeItem(key);
      }
    });
    this.cachedRates.clear();
  }

  /**
   * Update fallback rates from successful API calls
   */
  private updateFallbackFromLiveRate(from: string, to: string, rate: number): void {
    const key = `${from}_TO_${to}`;
    
    if (key === 'USD_TO_GEL') {
      this.fallbackRates.USD_TO_GEL = rate;
      this.fallbackRates.GEL_TO_USD = Math.round((1 / rate) * 1000) / 1000;
      this.hasValidFallback = true;
    } else if (key === 'GEL_TO_USD') {
      this.fallbackRates.GEL_TO_USD = rate;
      this.fallbackRates.USD_TO_GEL = Math.round((1 / rate) * 1000) / 1000;
      this.hasValidFallback = true;
    }
    
    // Save to localStorage for persistence
    try {
      localStorage.setItem('currency_fallback_rates', JSON.stringify({
        rates: this.fallbackRates,
        timestamp: Date.now(),
        hasValid: this.hasValidFallback
      }));
    } catch (error) {
      console.error('Failed to save fallback rates:', error);
    }
    
    console.log('💱 Updated fallback rates from live API:', this.fallbackRates);
  }

  /**
   * Load fallback rates from localStorage
   */
  private loadFallbackRates(): void {
    try {
      const stored = localStorage.getItem('currency_fallback_rates');
      if (stored) {
        const parsed = JSON.parse(stored);
        const now = Date.now();
        
        // Use stored fallback if less than 7 days old
        if (now - parsed.timestamp < 7 * 24 * 60 * 60 * 1000) {
          this.fallbackRates = parsed.rates;
          this.hasValidFallback = parsed.hasValid && parsed.rates.USD_TO_GEL > 0;
          console.log('📁 Loaded fallback rates from storage:', this.fallbackRates);
        }
      }
    } catch (error) {
      console.error('Failed to load fallback rates:', error);
    }
  }

  /**
   * Initialize the service (call this on app startup)
   */
  async initialize(): Promise<void> {
    // Load any stored fallback rates
    this.loadFallbackRates();
    
    // Try to fetch fresh rates if we don't have valid fallbacks
    if (!this.hasValidFallback) {
      try {
        console.log('🔄 Initializing with fresh exchange rates...');
        await this.getExchangeRate('USD', 'GEL');
      } catch (_error) {
        console.warn('Failed to initialize with fresh rates, will try again on first conversion');
      }
    }
  }
}

// Export singleton instance
export const currencyService = new CurrencyService();

// Export types
export type { CurrencyConversionResult };
